import React from 'react';
import styled from 'styled-components';

const CaseStudyContainer = styled.section`
  id: case-study;
  padding: ${({ theme }) => `${theme.spacing.content.section} ${theme.spacing.content.base}`};
  background-image: url('/images/racking-sensor-visualization.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  /* Light semi-transparent overlay for text readability */
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.75);
    z-index: 0;
  }

  /* All child elements need to be above the overlay */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: ${({ theme }) => `${theme.spacing['3xl']} ${theme.spacing.content.narrow}`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => `${theme.spacing['2xl']} ${theme.spacing.content.narrow}`};
  }
`;

const CaseStudyContent = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.wide};
  margin: 0 auto;
  text-align: center;
`;

const CaseStudyTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }
`;

const CaseStudySubtitle = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing['3xl']} 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const MainCaseStudy = styled.div`
  background: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing['3xl']};
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['3xl']};
  text-align: left;
  box-shadow: ${({ theme }) => theme.shadows['2xl']};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ theme }) => theme.colors.status.success};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing['2xl']};
    text-align: center;
  }
`;

const CaseStudyHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px 200px;
  gap: ${({ theme }) => theme.spacing['2xl']};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr 200px;
    gap: ${({ theme }) => theme.spacing.xl};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
    text-align: center;
  }
`;

const CaseStudyInfo = styled.div``;

const ProjectTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
  }
`;

const ProjectTagline = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.light};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  opacity: 0.9;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const ClientLogos = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.base};
  justify-content: flex-start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    justify-content: center;
  }
`;

const ClientLogo = styled.div`
  background: rgba(255, 255, 255, 0.2);
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ProductImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  
  img {
    max-width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    box-shadow: ${({ theme }) => theme.shadows.xl};
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    img {
      height: 150px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    order: 1;
    img {
      height: 180px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    img {
      height: 150px;
    }
  }
`;

const ImpactMetric = styled.div`
  text-align: center;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  backdrop-filter: blur(10px);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const MetricNumber = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize['5xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.black};
  line-height: 1;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize['4xl']};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize['3xl']};
  }
`;

const MetricLabel = styled.div`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.wide};
  color: ${({ theme }) => theme.colors.text.primary};
  opacity: 0.9;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const ProjectDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing['2xl']};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

const DetailSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border-left: 4px solid ${({ theme }) => theme.colors.accent};
  backdrop-filter: blur(10px);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const DetailTitle = styled.h5`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.base} 0;
  color: ${({ theme }) => theme.colors.accent};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const DetailContent = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0;
  opacity: 0.95;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const AchievementsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin-bottom: ${({ theme }) => theme.spacing['2xl']};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const AchievementCard = styled.div`
  background: ${({ theme }) => theme.colors.primary.main};
  color: ${({ theme }) => theme.colors.text.inverse};
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 10px 25px rgba(26, 43, 66, 0.3), 0 6px 10px rgba(26, 43, 66, 0.2);
  transition: all ${({ theme }) => theme.transitions.base};
  text-align: center;

  &:hover {
    transform: translateY(-4px);
    background: ${({ theme }) => theme.colors.primary.light};
    box-shadow: 0 15px 35px rgba(26, 43, 66, 0.4), 0 10px 15px rgba(26, 43, 66, 0.3);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const AchievementIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.primary.main};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.base} auto;
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 50px;
    height: 50px;
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }
`;

const AchievementTitle = styled.h6`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: ${({ theme }) => theme.colors.text.inverse};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const AchievementDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  margin: 0;
  color: rgba(255, 255, 255, 0.85);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const QuoteSection = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: ${({ theme }) => theme.spacing['2xl']};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border-left: 4px solid ${({ theme }) => theme.colors.accent};
  text-align: center;
  backdrop-filter: blur(10px);

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.xl};
  }
`;

const Quote = styled.blockquote`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  font-style: italic;
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  color: ${({ theme }) => theme.colors.text.primary};

  &:before {
    content: '"';
    font-size: 2em;
    line-height: 0;
    margin-right: 0.1em;
    vertical-align: -0.4em;
  }

  &:after {
    content: '"';
    font-size: 2em;
    line-height: 0;
    margin-left: 0.1em;
    vertical-align: -0.4em;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${({ theme }) => theme.typography.fontSize.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.base};
  }
`;

const QuoteAttribution = styled.p`
  font-family: ${({ theme }) => theme.typography.fontFamily.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin: 0;
  opacity: 0.8;
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
  }
`;

const CaseStudySection = () => {
  console.log('CaseStudySection updated - contrast and colors fixed');
  return (
    <CaseStudyContainer id="case-study">
      <CaseStudyContent>
        <CaseStudyTitle>Transforming Manufacturing Operations at Global Scale</CaseStudyTitle>
        <CaseStudySubtitle>
          Real-world results demonstrating comprehensive capability to solve complex manufacturing 
          challenges requiring both technical depth and strategic business understanding.
        </CaseStudySubtitle>

        <MainCaseStudy>
          <CaseStudyHeader>
            <CaseStudyInfo>
              <ProjectTitle>A-SAFE RackEye IoT System</ProjectTitle>
              <ProjectTagline>
                Complete IoT system from sensor installation through predictive analytics, 
                deployed across Fortune 500 global manufacturing facilities.
              </ProjectTagline>
              <ClientLogos>
                <ClientLogo>AMAZON</ClientLogo>
                <ClientLogo>LAND ROVER</ClientLogo>
                <ClientLogo>NESTLÉ</ClientLogo>
              </ClientLogos>
            </CaseStudyInfo>
            <ProductImage>
              <img 
                src="/images/case-studies/a-safe-rackeye-system.jpg" 
                alt="A-SAFE RackEye IoT System - Manufacturing safety technology deployment"
              />
            </ProductImage>
            <ImpactMetric>
              <MetricNumber>85%</MetricNumber>
              <MetricLabel>Accident Reduction<br />Globally</MetricLabel>
            </ImpactMetric>
          </CaseStudyHeader>

          <ProjectDetails>
            <DetailSection>
              <DetailTitle>Challenge</DetailTitle>
              <DetailContent>
                Warehouse accidents across Fortune 500 global manufacturing facilities were creating 
                significant safety risks, operational disruption, and liability exposure. Traditional 
                reactive safety measures were insufficient for the scale and complexity of operations.
              </DetailContent>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Solution</DetailTitle>
              <DetailContent>
                Complete IoT system architecture from fundamental sensors through advanced predictive 
                analytics. Designed end-to-end solution encompassing hardware deployment, data 
                collection, analysis algorithms, and actionable insights dashboard.
              </DetailContent>
            </DetailSection>

            <DetailSection>
              <DetailTitle>Implementation</DetailTitle>
              <DetailContent>
                Successfully navigated Fortune 500 procurement, compliance, and deployment processes. 
                Managed global rollout across diverse manufacturing environments while maintaining 
                operational continuity and meeting strict safety standards.
              </DetailContent>
            </DetailSection>
          </ProjectDetails>

          <QuoteSection>
            <Quote>
              This demonstrates the rare combination of deep technical capability and strategic 
              business understanding required to transform complex manufacturing operations at enterprise scale.
            </Quote>
            <QuoteAttribution>
              Strategic Technology Leadership Assessment
            </QuoteAttribution>
          </QuoteSection>
        </MainCaseStudy>

        <AchievementsGrid>
          <AchievementCard>

            <AchievementTitle>System Architecture Mastery</AchievementTitle>
            <AchievementDescription>
              From fundamental sensors through advanced analytics, demonstrating comprehensive 
              capability to architect complete solutions for complex manufacturing challenges.
            </AchievementDescription>
          </AchievementCard>

          <AchievementCard>

            <AchievementTitle>Global Enterprise Scaling</AchievementTitle>
            <AchievementDescription>
              Successfully navigated Fortune 500 procurement, compliance, and deployment processes, 
              proving ability to operate effectively within large-scale manufacturing organizations.
            </AchievementDescription>
          </AchievementCard>

          <AchievementCard>

            <AchievementTitle>Quantifiable Impact</AchievementTitle>
            <AchievementDescription>
              85% reduction in warehouse accidents represents substantial risk mitigation, 
              operational efficiency improvements, and competitive advantage through advanced IoT capabilities.
            </AchievementDescription>
          </AchievementCard>

          <AchievementCard>

            <AchievementTitle>Crisis-to-Advantage Transformation</AchievementTitle>
            <AchievementDescription>
              Transformed critical safety challenges into competitive advantages through innovative 
              technology deployment, demonstrating the strategic value of comprehensive IT/OT integration.
            </AchievementDescription>
          </AchievementCard>
        </AchievementsGrid>
      </CaseStudyContent>
    </CaseStudyContainer>
  );
};

export default CaseStudySection;